import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormGroup
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as ShoppingCartIcon,
  LocationOn as LocationOnIcon,
  MyLocation as MyLocationIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  Diamond as DiamondIcon,
  LocalOffer as CouponIcon,
  AttachMoney as MoneyIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  LocalShipping as ShippingIcon,
  Receipt as ReceiptIcon,
  Security as SecurityIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import EnhancedLocationDialog from '../components/EnhancedLocationDialog';
import BottomNavigation from '../components/BottomNavigation';
import { orderAPI, paymentAPI, addressAPI, walletAPI } from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import useResponsive from '../hooks/useResponsive';
import { createStripePromise } from '../utils/stripeUtils';
import { formatCurrency, getCountryCode, getCountryFields } from '../utils/countryUtils';

// Initialize Stripe using utility function (no hardcoded fallback)
const stripePromise = createStripePromise();



const ProductCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { provider, cartItems } = location.state || {};
  const { clearProviderCart, clearCart } = useCart();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  
  // Get country code for currency formatting
  const countryCode = getCountryCode();

  // State management
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletGems, setWalletGems] = useState(0);
  const [appliedBalance, setAppliedBalance] = useState(0);
  const [appliedGems, setAppliedGems] = useState(0);
  const [remainingGems, setRemainingGems] = useState(0);
  const [paymentOption, setPaymentOption] = useState('none');
  const [grandTotal, setGrandTotal] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [shippingFee, setShippingFee] = useState(11);

  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [shippingAddress, setShippingAddress] = useState('');
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [saveCard, setSaveCard] = useState(true);
  const [selectedSavedCard, setSelectedSavedCard] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(true); // Set to true to show payment form by default
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);


  // Calculate grand total
  const calculateGrandTotal = () => {
    let total = subTotal + shippingFee - couponDiscount - appliedBalance;
    // Convert gems to dollar value: 1000 gems = $10
    const gemsDiscount = (appliedGems / 1000) * 10;
    total -= gemsDiscount;
    setGrandTotal(Math.max(0, total));
  };

  // Check if checkout is allowed (total must be > 0)
  const isCheckoutAllowed = () => {
    const total = subTotal + shippingFee - couponDiscount - appliedBalance - ((appliedGems / 1000) * 10);
    return total > 0;
  };



  // Format price helper function
  const formatPrice = (price) => {
    if (!price) return formatCurrency(0);
    if (typeof price === 'string' && (price.includes('$') || price.includes('₹'))) {
      return price;
    }
    return formatCurrency(parseFloat(price) || 0);
  };

  // Load saved payment methods
  const loadSavedCards = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      
      // PRODUCTION: Already using production URL, but ensuring consistency
      // const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/customer/payments/saved-methods`, { // COMMENTED - Already production
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/customer/payments/saved-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedCards(data.paymentMethods || []);
      } else {
        console.error('Error fetching saved methods:', response.status);
      }
    } catch (error) {
      console.error('Error loading saved cards:', error);
    }
  };

  // Load wallet balance
  const loadWalletBalance = async () => {
    try {
      const response = await walletAPI.getWalletBalance();
      if (response.success) {
        setWalletBalance(response.data.balance || 0);
        setWalletGems(response.data.gems || 0);
        setRemainingGems(response.data.gems || 0);
      }
    } catch (error) {
      console.error('Error loading wallet balance:', error);
      // Set default values on error
      setWalletBalance(0);
      setWalletGems(0);
      setRemainingGems(0);
    }
  };

  // Load saved addresses and set default location from home page
  const loadSavedAddresses = async () => {
    try {
      // First, try to get the location from localStorage (same as home page)
      const savedLocation = localStorage.getItem('userLocation');
      if (savedLocation) {
        try {
          const locationData = JSON.parse(savedLocation);
        //  console.log('📍 Using location from home page:', locationData);
          setShippingAddress(locationData.address || locationData.fullAddress);
        } catch (e) {
          console.error('Error parsing saved location:', e);
        }
      }

      // Then load saved addresses from database
      const response = await addressAPI.getAddresses();
      
      if (response.success && response.data && response.data.length > 0) {
        setSavedAddresses(response.data);
        // If no location from localStorage, use the default saved address
        if (!savedLocation) {
          const defaultAddress = response.data.find(addr => addr.isDefault) || response.data[0];
          setSelectedAddressId(defaultAddress._id);
          
          // Format the address like in the Addresses page
          const formattedAddress = `${defaultAddress.street}, ${defaultAddress.city}, ${defaultAddress.state} ${defaultAddress.zipCode}`;
          setShippingAddress(formattedAddress);
        }
      } else {
      //  console.log('⚠️ No saved addresses found or empty response');
        // If no saved addresses and no localStorage location, show empty
        if (!savedLocation) {
          setShippingAddress('');
        }
      }
    } catch (error) {
      console.error('❌ Error loading saved addresses:', error);
      // Fallback to localStorage location only
      const savedLocation = localStorage.getItem('userLocation');
      if (savedLocation) {
        try {
          const locationData = JSON.parse(savedLocation);
        //  console.log('📍 Using saved location from localStorage as fallback:', locationData);
          setShippingAddress(locationData.address || locationData.fullAddress);
        } catch (e) {
          console.error('Error parsing saved location:', e);
        }
      }
    }
  };


  // Load initial data
  useEffect(() => {
    if (provider && cartItems) {
      initializeCheckout();
      loadSavedCards();
      loadWalletBalance();
      loadSavedAddresses();
    }
  }, [provider, cartItems]);

  useEffect(() => {
    calculateGrandTotal();
  }, [subTotal, shippingFee, couponDiscount, appliedBalance, appliedGems, paymentOption]);


  const initializeCheckout = () => {
    // Calculate subtotal from cart items with proper price parsing
    const total = cartItems.reduce((sum, item) => {
      let price = item.price || 0;
      
      // Parse price properly - handle both string and number formats
      if (typeof price === 'string') {
        // Remove $ and any commas, then parse as float
        price = parseFloat(price.replace(/[$,]/g, '')) || 0;
      } else {
        // If it's already a number, use it directly
        price = parseFloat(price) || 0;
      }
      
      return sum + (price * (item.quantity || 1));
    }, 0);
    setSubTotal(total);

    // Set default shipping address from user profile or location state
    setShippingAddress(location.state?.selectedAddress || '');
  };

  const handleWalletOptionChange = (option) => {
    setPaymentOption(option);
    
    if (option === 'balance') {
      // Wallet Balance: 80% rule applies
      const tempGrandTotal = subTotal + shippingFee - couponDiscount;
      const maxWalletUsage = tempGrandTotal * 0.8; // 80% of order total
      const amountToApply = Math.min(walletBalance, maxWalletUsage);
      setAppliedBalance(amountToApply);
      setAppliedGems(0);
      setRemainingGems(walletGems); // Reset remaining gems
    } else if (option === 'gems') {
      // Gems: Fixed 1000 gems per booking (not 80% rule)
      // Check if user has at least 1000 gems
      if (walletGems < 1000) {
        setAppliedGems(0);
        setAppliedBalance(0);
        setRemainingGems(walletGems);
        return;
      }
      
      // Apply exactly 1000 gems = discount (amount calculated dynamically)
      setAppliedGems(1000);
      setAppliedBalance(0);
      setRemainingGems(walletGems - 1000); // Deduct 1000 gems from wallet
    } else {
      setAppliedBalance(0);
      setAppliedGems(0);
      setRemainingGems(walletGems); // Reset remaining gems
    }
  };

  const handleCouponApply = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      setLoading(true);
      setCouponError(''); // Clear previous errors
      // PRODUCTION: Already using production URL, but ensuring consistency
      // const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/offers/validate-coupon`, { // COMMENTED - Already production
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/offers/validate-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('customerToken')}`
        },
        body: JSON.stringify({
          couponCode: couponCode.trim(),
          orderAmount: subTotal,
          orderType: 'product'
        })
      });

      if (response.ok) {
        const data = await response.json();
      //  console.log('Coupon validation response:', data);
        if (data.success) {
          setCouponDiscount(data.data.discountAmount);
          setCouponError(''); // Clear errors on success
        } else {
          setCouponError(data.message || 'Invalid coupon code');
        }
      } else {
        const errorData = await response.json();
        console.error('Coupon validation error:', errorData);
        setCouponError(errorData.message || 'Failed to apply coupon');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setCouponError('Failed to apply coupon - please try again');
    } finally {
      setLoading(false);
    }
  };


  const handleSelectSavedAddress = (address) => {
    setSelectedAddressId(address._id);
    // Format the address like in the Addresses page
    const formattedAddress = `${address.street}, ${address.city}, ${address.state} ${address.zipCode}`;
    setShippingAddress(formattedAddress);
    
    // Update localStorage to keep it in sync with home page
    const locationData = {
      address: formattedAddress,
      fullAddress: formattedAddress,
      latitude: address.latitude || 0,
      longitude: address.longitude || 0,
      type: 'saved'
    };
    localStorage.setItem('userLocation', JSON.stringify(locationData));
  };


     

  const handlePaymentSuccess = async (paymentResult) => {
    setIsProcessing(true);
    
    try {
      // Clear all cart data after successful order
      if (provider?.id) {
        clearProviderCart(provider.id);
      }
      
      // Also clear the global customer cart
      try {
        await clearCart();
      } catch (cartError) {
        console.error('Error clearing global cart:', cartError);
        // Don't fail the order if cart clearing fails
      }
      

      
      // Use real order data from backend response
      const orderData = paymentResult?.orderDetails || paymentResult;
      
      // Set the order data for success screen using real backend data
      setCreatedOrderId(orderData?.orderId || orderData?.orderNumber || Date.now());
      
      // Navigate to order success page with complete order details
      navigate('/order-success', { 
        state: { 
          orderData: paymentResult // Pass the complete payment result
        } 
      });
      
      // Refresh saved cards after successful payment
      if (saveCard) {
        loadSavedCards();
      }
    } catch (error) {
      console.error('❌ Error processing payment success:', error);
      
      // For demo purposes, if backend fails, still navigate to success page
      navigate('/order-success', { 
        state: { 
          orderData: paymentResult || { orderNumber: `DEMO-${Date.now()}` } 
        } 
      });
    } finally {
      setIsProcessing(false);
      setShowPaymentForm(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setIsProcessing(false);
    // Convert error to string to avoid React rendering issues
    const errorMessage = error instanceof Error ? error.message : String(error);
    setPaymentError(errorMessage);
    setShowErrorDialog(true);
  };

  const handlePaymentCancel = () => {
    setIsProcessing(false);
  };



  if (!provider || !cartItems) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          No checkout data available
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/store')}
          startIcon={<ArrowBackIcon />}
        >
          Back to Store
        </Button>
      </Box>
    );
  }

    return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      py: 4,
      pb: { xs: 12, sm: 4 } // Extra padding at bottom for mobile to account for bottom navigation
    }}>
             {/* Header */}
       <Box sx={{ 
         maxWidth: 1200,
         mx: 'auto',
         px: { xs: 2, md: 4 },
         mb: 3
       }}>
         <Box sx={{ 
           display: 'flex', 
           alignItems: 'center', 
           background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
           color: 'white',
           p: 2,
           borderRadius: 2,
           boxShadow: '0 4px 20px rgba(26, 35, 126, 0.3)',
           backdropFilter: 'blur(10px)',
           border: '1px solid rgba(255, 255, 255, 0.2)'
         }}>
           <IconButton
             onClick={() => navigate(-1)}
             sx={{ 
               mr: 2, 
               color: 'white',
               background: 'rgba(255, 255, 255, 0.1)',
               backdropFilter: 'blur(10px)',
               border: '1px solid rgba(255, 255, 255, 0.2)',
               '&:hover': {
                 background: 'rgba(255, 255, 255, 0.2)',
                 transform: 'scale(1.05)'
               },
               transition: 'all 0.3s ease'
             }}
           >
             <ArrowBackIcon />
           </IconButton>
           <Box sx={{ flexGrow: 1 }}>
             <Typography variant="h5" sx={{ 
               fontWeight: 700, 
               color: 'white',
               textShadow: '0 2px 4px rgba(0,0,0,0.1)'
             }}>
               Secure Checkout
             </Typography>
             <Typography variant="body2" sx={{ 
               color: 'rgba(255, 255, 255, 0.9)',
               mt: 0.25
             }}>
               Complete your purchase with confidence
             </Typography>
           </Box>
           <Box sx={{ textAlign: 'right' }}>
             <Typography variant="h6" sx={{ 
               fontWeight: 700, 
               color: 'white',
               textShadow: '0 2px 4px rgba(0,0,0,0.1)'
             }}>
               {formatCurrency(grandTotal)}
             </Typography>
             <Typography variant="body2" sx={{ 
               color: 'rgba(255, 255, 255, 0.9)',
               mt: 0.25
             }}>
               Total Amount
             </Typography>
           </Box>
         </Box>
       </Box>

      {/* Content */}
      <Box sx={{ 
        maxWidth: 1200,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        display: 'flex', 
        gap: 4, 
        flexDirection: { xs: 'column', lg: 'row' }
      }}>
        {/* Left Column - Order Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Products Card */}
          <Card sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden'
          }}>
            <Box sx={{
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              p: 2,
              color: 'white'
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: '1rem'
              }}>
                <ShoppingCartIcon sx={{ fontSize: 20 }} />
                Your Cart Items
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                mt: 0.25
              }}>
                {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {cartItems.map((item, index) => (
                  <Box key={index} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    border: '1px solid #e9ecef',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                      borderColor: '#1a237e'
                    }
                  }}>
                    {/* Product Photo */}
                    <Box
                      component="img"
                      src={item.image || item.photo || 'https://via.placeholder.com/60x60?text=Product'}
                      alt={item.name}
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        borderRadius: 1.5, 
                        objectFit: 'cover',
                        border: '1px solid #e9ecef',
                        mr: 2
                      }}
                    />
                    
                    {/* Product Details */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 600, 
                        mb: 0.25,
                        color: '#2c3e50',
                        fontSize: '0.9rem'
                      }}>
                        {item.productName || item.name}
                      </Typography>
                      
                      {/* Quantity Badge */}
                      <Box sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                        color: 'white',
                        px: 1.5,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>
                        Qty: {item.quantity || 1}
                      </Box>
                    </Box>
                    
                    {/* Price */}
                    <Typography variant="body1" sx={{ 
                      fontWeight: 700, 
                      color: '#1a237e',
                      fontSize: '1rem'
                    }}>
                      {(() => {
                        let price = item.price || 0;
                        if (typeof price === 'string') {
                          price = parseFloat(price.replace(/[$,]/g, '')) || 0;
                        } else {
                          price = parseFloat(price) || 0;
                        }
                        return formatPrice(price * (item.quantity || 1));
                      })()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

                     {/* Offer Section */}
          <Card sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden'
          }}>
            <Box sx={{
              background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
              p: 2,
              color: 'white'
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: '1rem'
              }}>
                <CouponIcon sx={{ fontSize: 20 }} />
                Offers & Discounts
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                mt: 0.25
              }}>
                Save money with exclusive offers
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 2 }}>
              {/* Coupon Section */}
              <Box>
                <Typography variant="body2" sx={{ 
                  mb: 1.5, 
                  fontWeight: 600,
                  color: '#2c3e50'
                }}>
                  Apply Coupon Code
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    size="small"
                    placeholder="Enter your coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    sx={{ 
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover fieldset': {
                          borderColor: '#e91e63',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#e91e63',
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCouponApply}
                    disabled={loading || !couponCode.trim()}
                    sx={{
                      background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
                      borderRadius: 1.5,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      boxShadow: '0 2px 10px rgba(233, 30, 99, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #c2185b 0%, #e91e63 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 15px rgba(233, 30, 99, 0.4)'
                      },
                      '&:disabled': {
                        background: '#e0e0e0',
                        transform: 'none',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={16} color="inherit" /> : 'Apply'}
                  </Button>
                </Box>
                {couponDiscount > 0 && (
                  <Alert severity="success" sx={{ 
                    mt: 1.5,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                    border: '1px solid #c3e6cb',
                    '& .MuiAlert-icon': {
                      color: '#155724'
                    }
                  }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      🎉 Coupon applied successfully! 
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      You saved: <strong>{formatCurrency(couponDiscount)}</strong>
                    </Typography>
                  </Alert>
                )}
                {couponError && (
                  <Alert severity="error" sx={{ 
                    mt: 1.5,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                    border: '1px solid #f5c6cb',
                    '& .MuiAlert-icon': {
                      color: '#721c24'
                    }
                  }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      ❌ {couponError}
                    </Typography>
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

                     {/* Wallet Section */}
          <Card sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden'
          }}>
                         <Box sx={{
               background: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
               p: 2,
               color: 'white'
             }}>
               <Typography variant="subtitle1" sx={{ 
                 fontWeight: 600, 
                 display: 'flex', 
                 alignItems: 'center', 
                 gap: 1,
                 fontSize: '1rem'
               }}>
                 <WalletIcon sx={{ fontSize: 20 }} />
                 Wallet & Gems
               </Typography>
               <Typography variant="body2" sx={{ 
                 color: 'rgba(255, 255, 255, 0.9)',
                 mt: 0.25
               }}>
                 Use your wallet balance or gems to reduce the total amount
               </Typography>
             </Box>
            
            <CardContent sx={{ p: 2 }}>
                             {/* Wallet Options */}
               <Box sx={{ mb: 3 }}>
                 <Typography variant="body2" sx={{ 
                   mb: 1, 
                   fontWeight: 600,
                   color: '#2c3e50'
                 }}>
                   Available Balance
                 </Typography>
                 <Typography variant="caption" sx={{ 
                   color: '#666',
                   fontStyle: 'italic',
                   display: 'block',
                   mb: 2,
                   fontSize: '0.75rem'
                 }}>
                   💡 You can use up to 80% of your order total from wallet/gems
                 </Typography>
                 <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <RadioGroup
                    value={paymentOption}
                    onChange={(e) => {
                      handleWalletOptionChange(e.target.value);
                    }}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    <FormControlLabel 
                      value="none" 
                      control={
                        <Radio 
                          onClick={() => {
                            handleWalletOptionChange('none');
                          }}
                          sx={{
                            '&.Mui-checked': {
                              color: '#00bcd4',
                            },
                          }} 
                        />
                      } 
                      label={
                        <Box sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: paymentOption === 'none' ? '2px solid #00bcd4' : '2px solid #e9ecef',
                          background: paymentOption === 'none' ? 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' : '#f8f9fa',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#00bcd4',
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
                          }
                        }}>
                          <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.9rem' }}>
                            None
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Pay with card only
                          </Typography>
                        </Box>
                      }
                      sx={{ 
                        flex: 1,
                        m: 0,
                        '& .MuiFormControlLabel-label': {
                          width: '100%'
                        }
                      }}
                    />
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1.5
                    }}>
                      <FormControlLabel 
                        value="balance" 
                        control={
                        <Radio 
                          onClick={() => {
                            handleWalletOptionChange('balance');
                          }}
                            sx={{
                              '&.Mui-checked': {
                                color: '#00bcd4',
                              },
                            }} 
                          />
                        } 
                        label={
                          <Box sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            border: paymentOption === 'balance' ? '2px solid #00bcd4' : '2px solid #e9ecef',
                            background: paymentOption === 'balance' ? 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' : '#f8f9fa',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            opacity: walletBalance <= 0 ? 0.5 : 1,
                            '&:hover': {
                              borderColor: walletBalance <= 0 ? '#e9ecef' : '#00bcd4',
                              background: walletBalance <= 0 ? '#f8f9fa' : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
                            }
                          }}>
                            <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.9rem' }}>
                              Wallet Balance
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              {formatCurrency(walletBalance)} available
                            </Typography>
                            {walletBalance > 0 && (
                              <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                                💰 Up to 80% of order total
                              </Typography>
                            )}
                          </Box>
                        }
                        disabled={walletBalance <= 0}
                        sx={{ 
                          flex: 1,
                          m: 0,
                          '& .MuiFormControlLabel-label': {
                            width: '100%'
                          }
                        }}
                      />
                      <FormControlLabel 
                        value="gems" 
                        control={
                          <Radio 
                            onClick={() => {
                              handleWalletOptionChange('gems');
                            }}
                            sx={{
                              '&.Mui-checked': {
                                color: '#00bcd4',
                              },
                            }} />
                        } 
                        label={
                          <Box 
                            onClick={() => {
                              if (walletGems >= 1000) {
                                handleWalletOptionChange('gems');
                              }
                            }}
                            sx={{
                              p: 1.5,
                              borderRadius: 1.5,
                              border: paymentOption === 'gems' ? '2px solid #00bcd4' : '2px solid #e9ecef',
                              background: paymentOption === 'gems' ? 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' : '#f8f9fa',
                              transition: 'all 0.3s ease',
                              cursor: walletGems >= 1000 ? 'pointer' : 'not-allowed',
                              opacity: walletGems >= 1000 ? 1 : 0.5,
                              '&:hover': {
                                borderColor: walletGems >= 1000 ? '#00bcd4' : '#e9ecef',
                                background: walletGems >= 1000 ? 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' : '#f8f9fa'
                              }
                            }}>
                            <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.9rem' }}>
                              Gems
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              {paymentOption === 'gems' ? remainingGems : walletGems} gems available
                            </Typography>
                            {walletGems < 1000 && (
                              <Typography variant="caption" sx={{ color: '#f44336', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                                Minimum 1000 gems required
                              </Typography>
                            )}
                            {walletGems >= 1000 && (
                              <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                                💎 1000 gems = {formatCurrency(10)} discount
                              </Typography>
                            )}
                          </Box>
                        }
                        disabled={walletGems < 1000}
                        sx={{ 
                          flex: 1,
                          m: 0,
                          '& .MuiFormControlLabel-label': {
                            width: '100%'
                          }
                        }}
                      />
                    </Box>
                  </RadioGroup>
                </FormControl>
                {(walletBalance <= 0 && walletGems <= 0) && (
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                    border: '1px solid #ffeaa7'
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#856404',
                      fontStyle: 'italic',
                      textAlign: 'center'
                    }}>
                      💡 No wallet balance or gems available. You can add funds to your wallet anytime!
                    </Typography>
                  </Box>
                )}
              </Box>

                             {/* Note: Saved cards are now displayed in the Stripe payment form below */}
            </CardContent>
          </Card>

                                               {/* Delivery Address */}
             <Card sx={{ 
               mb: 3,
               borderRadius: 2,
               boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
               background: 'rgba(255, 255, 255, 0.95)',
               backdropFilter: 'blur(10px)',
               border: '1px solid rgba(255, 255, 255, 0.2)',
               overflow: 'hidden'
             }}>
               <Box sx={{
                 background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                 p: 2,
                 color: 'white'
               }}>
                 <Typography variant="subtitle1" sx={{ 
                   fontWeight: 600, 
                   display: 'flex', 
                   alignItems: 'center', 
                   gap: 1,
                   fontSize: '1rem'
                 }}>
                   <LocationOnIcon sx={{ fontSize: 20 }} />
                   Delivery Address
                 </Typography>
                 <Typography variant="body2" sx={{ 
                   color: 'rgba(255, 255, 255, 0.9)',
                   mt: 0.25
                 }}>
                   Where should we deliver your order?
                 </Typography>
               </Box>
               
                               <CardContent sx={{ p: 3 }}>
                  {/* Saved Addresses Section */}
                  {savedAddresses.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ 
                        mb: 2, 
                        fontWeight: 600,
                        color: '#2c3e50'
                      }}>
                        Your Saved Addresses
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {savedAddresses.map((address) => (
                          <Box key={address._id} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 2,
                            borderRadius: 2,
                            border: selectedAddressId === address._id ? '2px solid #2196f3' : '1px solid #e9ecef',
                            background: selectedAddressId === address._id ? 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' : '#f8f9fa',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: '#2196f3',
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
                            }
                          }} onClick={() => handleSelectSavedAddress(address)}>
                            <LocationOnIcon sx={{ 
                              color: '#2196f3', 
                              mr: 2,
                              fontSize: 24
                            }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.9rem' }}>
                                  {address.label || address.contactName || 'Address'}
                                </Typography>
                                {address.isDefault && (
                                  <Chip 
                                    label="Default" 
                                    size="small" 
                                    color="success" 
                                    sx={{ ml: 1, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ color: '#2c3e50', fontSize: '0.8rem' }}>
                                {address.street}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {address.city}, {address.state} {address.zipCode}
                              </Typography>
                            </Box>
                            <Radio
                              checked={selectedAddressId === address._id}
                              onChange={() => handleSelectSavedAddress(address)}
                              color="primary"
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Current Selected Address Display */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ 
                      mb: 2, 
                      fontWeight: 600,
                      color: '#2c3e50'
                    }}>
                      Selected Delivery Address
                    </Typography>
                    <Box sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                      border: '2px solid #e9ecef',
                      mb: 2
                    }}>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        color: '#2c3e50'
                      }}>
                        {shippingAddress || '📍 No address selected'}
                      </Typography>
                      {!shippingAddress && (
                        <Typography variant="body2" color="text.secondary">
                          Please select a delivery address to continue
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<LocationOnIcon />}
                      onClick={() => setShowLocationDialog(true)}
                      sx={{
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 4px 15px rgba(116, 185, 255, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(116, 185, 255, 0.4)'
                        }
                      }}
                    >
                      {shippingAddress ? 'Change Address' : 'Select Address'}
                    </Button>
                  </Box>
                </CardContent>
             </Card>

                           {/* Special Instructions */}
              <Card sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                overflow: 'hidden'
              }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                  p: 2,
                  color: 'white'
                }}>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: '1rem'
                  }}>
                    <MoneyIcon sx={{ fontSize: 20 }} />
                    Special Instructions
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    mt: 0.25
                  }}>
                    Any special requests for delivery?
                  </Typography>
                </Box>
               
               <CardContent sx={{ p: 3 }}>
                 <TextField
                   fullWidth
                   multiline
                   rows={4}
                   placeholder="Enter any special instructions for delivery (optional)..."
                   value={specialInstructions}
                   onChange={(e) => setSpecialInstructions(e.target.value)}
                   sx={{
                     '& .MuiOutlinedInput-root': {
                       borderRadius: 2,
                       background: '#f8f9fa',
                       '&:hover fieldset': {
                         borderColor: '#fd79a8',
                       },
                       '&.Mui-focused fieldset': {
                         borderColor: '#fd79a8',
                       },
                     },
                   }}
                 />
               </CardContent>
             </Card>
         </Box>

                                   {/* Right Column - Bill Summary and Payment */}
          <Box sx={{ width: { xs: '100%', lg: 400 }, flexShrink: 0 }}>
            {/* Bill Summary Card */}
            <Card sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              overflow: 'hidden',
              position: 'sticky',
              top: 20
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                p: 2,
                color: 'white'
              }}>
                <Typography variant="subtitle1" sx={{ 
                  fontWeight: 600,
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <ReceiptIcon sx={{ fontSize: 20 }} />
                  Order Summary
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  mt: 0.25
                }}>
                  Review your order details
                </Typography>
              </Box>
              
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50', fontSize: '0.9rem' }}>
                      Sub Total
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.9rem' }}>
                      {formatCurrency(subTotal)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50', fontSize: '0.9rem' }}>
                      Shipping Fee
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.9rem' }}>
                      {formatCurrency(shippingFee)}
                    </Typography>
                  </Box>
                  
                  {couponDiscount > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mb: 1.5, 
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                      border: '1px solid #c3e6cb'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#155724', fontSize: '0.85rem' }}>
                          🎉 Coupon Discount ({couponCode})
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => {
                            setCouponCode('');
                            setCouponDiscount(0);
                            setCouponError('');
                          }}
                          sx={{
                            minWidth: 'auto',
                            p: 0.5,
                            color: '#dc3545',
                            fontSize: '0.7rem',
                            '&:hover': {
                              backgroundColor: 'rgba(220, 53, 69, 0.1)'
                            }
                          }}
                        >
                          ✕ Remove
                        </Button>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#155724', fontSize: '0.85rem' }}>
                        -{formatCurrency(couponDiscount)}
                      </Typography>
                    </Box>
                  )}
                  
                  {appliedBalance > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mb: 1.5, 
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)',
                      border: '1px solid #bee5eb'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#0c5460', fontSize: '0.85rem' }}>
                        💳 Wallet Applied
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0c5460', fontSize: '0.85rem' }}>
                        -{formatCurrency(appliedBalance)}
                      </Typography>
                    </Box>
                  )}
                  
                  {appliedGems > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mb: 1.5, 
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                      border: '1px solid #ffeaa7'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#856404', fontSize: '0.85rem' }}>
                        💎 {appliedGems} Gems Applied
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#856404', fontSize: '0.85rem' }}>
                        -{formatCurrency((appliedGems / 1000) * 10)}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ 
                  my: 2,
                  borderColor: '#e9ecef',
                  borderWidth: 1.5
                }} />
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  p: 1.5,
                  borderRadius: 1.5,
                  background: !isCheckoutAllowed() ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' : 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                  color: 'white'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    Grand Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {formatCurrency(grandTotal)}
                  </Typography>
                </Box>
                
                {/* Warning when total is ≤ 0 */}
                {!isCheckoutAllowed() && (
                  <Alert severity="error" sx={{ 
                    mt: 2,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                    border: '1px solid #f44336',
                    '& .MuiAlert-icon': {
                      color: '#d32f2f'
                    }
                  }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      ⚠️ Order total must be greater than {formatCurrency(0)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      Please reduce your discounts or add more items to proceed
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>

                         {/* Terms and Conditions */}
             <Card sx={{ 
               mb: 3,
               borderRadius: 2,
               boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
               background: 'rgba(255, 255, 255, 0.95)',
               backdropFilter: 'blur(10px)',
               border: '1px solid rgba(255, 255, 255, 0.2)',
               overflow: 'hidden'
             }}>
               <CardContent sx={{ p: 2 }}>
                 <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                   <Box sx={{ flex: 1 }}>
                     <Typography variant="body2" color="text.secondary" sx={{ 
                       fontSize: '0.8rem',
                       lineHeight: 1.4,
                       color: '#666',
                       fontStyle: 'italic'
                     }}>
                       By placing this order, you agree to our Product Return & Refund Policy
                     </Typography>
                     <Button 
                       variant="text" 
                       size="small" 
                       sx={{ 
                         p: 0, 
                         minWidth: 'auto', 
                         textTransform: 'none',
                         color: '#1a237e',
                         fontWeight: 600,
                         textDecoration: 'underline',
                         fontSize: '0.8rem',
                         '&:hover': {
                           background: 'none',
                           color: '#0d47a1'
                         }
                       }}
                       onClick={(e) => {
                         e.preventDefault();
                         navigate('/product-return-policy');
                       }}
                     >
                       Read Policy
                     </Button>
                   </Box>
                 </Box>
               </CardContent>
             </Card>

                                                   {/* Stripe Payment Form */}
              <Card sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={() => {
                      if (!isCheckoutAllowed()) {
                        setPaymentError(`Order total must be greater than ${formatCurrency(0)}. Please adjust your discounts.`);
                        setShowErrorDialog(true);
                      } else if (grandTotal > 0) {
                        setShowStripeModal(true);
                      } else {
                        setPaymentError('Please add items to your cart');
                        setShowErrorDialog(true);
                      }
                    }}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      borderRadius: 2,
                      py: 2,
                      mb: { xs: 3, sm: 0 }, // Extra margin at bottom for mobile for better visibility
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                       boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                       '&:hover': {
                         background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
                         transform: 'translateY(-2px)',
                         boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)'
                       }
                     }}
                   >
                     Pay Now
                   </Button>
               </CardContent>
             </Card>
          </Box>
       </Box>

       {/* Enhanced Location Dialog */}
       <EnhancedLocationDialog
         open={showLocationDialog}
         onClose={() => setShowLocationDialog(false)}
         currentAddress={shippingAddress}
         onLocationSelect={(locationData) => {
           setShippingAddress(locationData.address);
           
           // Save location data to localStorage
           const locationInfo = {
             address: locationData.address,
             fullAddress: locationData.address,
             latitude: locationData.lat,
             longitude: locationData.lng,
             city: locationData.address ? locationData.address.split(',')[0] : '', // Extract city from address with null check
             type: locationData.type
           };
           localStorage.setItem('userLocation', JSON.stringify(locationInfo));
           
           setShowLocationDialog(false);
         }}
       />

        {/* Stripe Checkout Modal */}
        <Dialog 
          open={showStripeModal} 
          maxWidth="md" 
          fullWidth
          onClose={() => setShowStripeModal(false)}

          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              maxHeight: '90vh',
              overflow: 'hidden'
            },
            '& input[type="radio"]': {
              width: '16px !important',
              height: '16px !important',
              minWidth: '16px !important',
              minHeight: '16px !important',
              maxWidth: '16px !important',
              maxHeight: '16px !important',
              transform: 'scale(0.8) !important',
              accentColor: '#3b82f6 !important'
            },
            '& input[type="checkbox"]': {
              width: '16px !important',
              height: '16px !important',
              minWidth: '16px !important',
              minHeight: '16px !important',
              maxWidth: '16px !important',
              maxHeight: '16px !important',
              transform: 'scale(0.8) !important',
              accentColor: '#16a34a !important'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              💳 Secure Payment with Stripe
            </Typography>
            <IconButton
              onClick={() => setShowStripeModal(false)}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ height: '70vh', overflow: 'auto', p: 3 }}>
              {stripePromise ? (
                <Elements stripe={stripePromise} options={{
                mode: 'payment',
                amount: 1000,
                currency: getCountryFields(getCountryCode()).currency.toLowerCase(),
                paymentMethodCreation: 'manual',
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#1a237e',
                    colorBackground: '#ffffff',
                    colorText: '#1a1a1a',
                    colorDanger: '#ef4444',
                    colorSuccess: '#10b981',
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '12px',
                    fontSizeBase: '16px',
                  },
                },
              }}>
                                 <StripeCheckoutForm 
                   amount={grandTotal}
                   cartItems={cartItems}
                   provider={provider}
                   shippingAddress={shippingAddress}
                   specialInstructions={specialInstructions}
                   couponCode={couponCode}
                   couponDiscount={couponDiscount}
                   appliedBalance={appliedBalance}
                   appliedGems={appliedGems}
                   onSuccess={(result) => {
                  //   console.log('Payment successful:', result);
                     setShowStripeModal(false);
                     handlePaymentSuccess(result);
                   }}
                   onError={(error) => {
                     console.error('Payment failed:', error);
                     // Convert error to string to avoid React rendering issues
                     const errorMessage = error instanceof Error ? error.message : String(error);
                     setPaymentError(errorMessage);
                     setShowErrorDialog(true);
                   }}
                   onCancel={() => {
                     setShowStripeModal(false);
                   }}
                 />
              </Elements>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="error">
                  Stripe is not configured. Please check your environment variables.
                </Typography>
              </Box>
            )}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Payment Error Dialog */}
        <Dialog 
          open={showErrorDialog} 
          onClose={() => {
            setShowErrorDialog(false);
            setPaymentError(null);
          }}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              ⚠️ Payment Error
            </Typography>
            <IconButton
              onClick={() => {
                setShowErrorDialog(false);
                setPaymentError(null);
              }}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#ef4444', fontWeight: 600 }}>
                Payment Failed
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#374151', lineHeight: 1.6 }}>
                {paymentError || 'An unexpected error occurred during payment processing.'}
              </Typography>
              
              {/* Common Error Solutions */}
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: 2,
                border: '1px solid #f59e0b'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#92400e', mb: 1 }}>
                  💡 Common Solutions:
                </Typography>
                <Typography variant="body2" sx={{ color: '#92400e', textAlign: 'left' }}>
                  • Check your card details and ensure they are correct<br/>
                  • Verify your card has sufficient funds<br/>
                  • Try a different payment method<br/>
                  • Contact your bank if the issue persists<br/>
                  • Ensure your billing address matches your card
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => {
                setShowErrorDialog(false);
                setPaymentError(null);
              }}
              sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
                }
              }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setShowErrorDialog(false);
                setPaymentError(null);
                setShowStripeModal(false);
              }}
              sx={{
                borderColor: '#6b7280',
                color: '#6b7280',
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#374151',
                  color: '#374151',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

       {/* Bottom Navigation */}
       <BottomNavigation />
       </Box>
     );
   };

// Direct Payment Form Component using Stripe Elements
const DirectPaymentForm = ({ 
  onPaymentSuccess, 
  onPaymentError, 
  onCancel, 
  amount, 
  savedCards, 
  selectedSavedCard, 
  setSelectedSavedCard,
  saveCard,
  setSaveCard,
  shippingAddress,
  specialInstructions,
  cartItems,
  provider
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');



  const handlePayment = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Validate shipping address
    if (!shippingAddress) {
      setError('Please fill in your shipping address');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Submit the form to validate all fields
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message || 'Please check your payment details');
      }

      let paymentMethodId = selectedSavedCard;

      // If no saved card is selected, create a new payment method
      if (!selectedSavedCard) {
        const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: {
              name: 'Customer',
              email: 'customer@example.com',
            },
          },
        });

        if (paymentMethodError) {
          throw new Error(paymentMethodError.message || 'Failed to create payment method');
        }

        paymentMethodId = paymentMethod.id;
      }

      // Process payment directly and create order in one step
      // PRODUCTION: Already using production URL, but ensuring consistency
      // const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/customer/payments/process-direct`, { // COMMENTED - Already production
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/customer/payments/process-direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('customerToken')}`,
        },
        body: JSON.stringify({
          amount: Math.round(amount * 100), // Convert to cents
          currency: getCountryFields(getCountryCode()).currency.toLowerCase(),
          items: cartItems,
          customerEmail: 'customer@example.com',
          customerName: 'Customer',
          shippingAddress: shippingAddress,
          specialInstructions: specialInstructions,
          paymentMethodId: paymentMethodId,
           saveCard: selectedSavedCard ? false : saveCard, // Don't save if using saved card
          providerId: provider?._id,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Payment failed');
      }

      onPaymentSuccess(result.data);

    } catch (error) {
      console.error('❌ [CHECKOUT] Payment error:', error);
      setError(error instanceof Error ? error.message : 'Payment failed');
      onPaymentError?.(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePayment} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Error Messages */}
      {error && (
        <div style={{
          color: '#ff3b30',
          fontSize: '14px',
          margin: 0,
          padding: '8px 12px',
          background: '#fff5f5',
          borderRadius: '6px',
          border: '1px solid #fed7d7'
        }}>
          {error}
        </div>
      )}

      {/* Saved Payment Methods */}
       {savedCards && savedCards.length > 0 && (
        <div style={{
          marginBottom: '24px',
          padding: '20px',
          background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            💳 Saved Payment Methods
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
            {savedCards.map((card) => (
              <div 
                key={card.id} 
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  background: 'white',
                  borderRadius: '10px',
                  border: `2px solid ${selectedSavedCard === card.id ? '#3b82f6' : '#e2e8f0'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: selectedSavedCard === card.id ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : 'white',
                  transform: selectedSavedCard === card.id ? 'translateY(-1px)' : 'none'
                }}
                onClick={() => setSelectedSavedCard(card.id)}
                onMouseEnter={(e) => {
                  if (selectedSavedCard !== card.id) {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedSavedCard !== card.id) {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.transform = 'none';
                  }
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontWeight: 600, color: '#1f2937', textTransform: 'capitalize' }}>
                     {card.brand || card.card?.brand || 'Card'}
                  </span>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>
                     •••• {card.last4 || card.card?.last4 || '****'}
                  </span>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                     Expires {card.expMonth || card.card?.exp_month || '**'}/{card.expYear || card.card?.exp_year || '**'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input 
                    type="radio" 
                    name="selectedCard" 
                    value={card.id}
                    checked={selectedSavedCard === card.id}
                    onChange={() => setSelectedSavedCard(card.id)}
                    style={{
                      width: '16px !important',
                      height: '16px !important',
                      minWidth: '16px !important',
                      minHeight: '16px !important',
                      maxWidth: '16px !important',
                      maxHeight: '16px !important',
                      accentColor: '#3b82f6',
                      transform: 'scale(0.8)',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <button 
              type="button" 
              style={{
                background: 'none',
                border: '2px solid #3b82f6',
                color: '#3b82f6',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedSavedCard(null)}
              onMouseEnter={(e) => {
                e.target.style.background = '#3b82f6';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'none';
                e.target.style.color = '#3b82f6';
              }}
            >
              Use New Payment Method
            </button>
          </div>
        </div>
      )}

      {/* New Payment Method */}
      {!selectedSavedCard && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {savedCards.length > 0 ? '💳 Add New Payment Method' : '💳 Payment Details'}
          </h3>
                                           <div style={{
              border: '2px solid #e2e8f0',
              borderRadius: '16px',
              padding: '24px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s ease'
            }}>
                                        
            <PaymentElement 
              options={{
                layout: 'tabs',
                paymentMethodOrder: [
                  'card',
                  'apple_pay',
                  'google_pay',
                  'afterpay_clearpay',
                  'klarna',
                  'alipay',
                  'zip',
                  'paypal',
                  'link'
                ],
                defaultValues: {
                  billingDetails: {
                    name: 'Customer',
                    email: 'customer@example.com',
                  }
                },
                fields: {
                  billingDetails: {
                    name: 'auto',
                    email: 'auto',
                    phone: 'auto',
                    address: {
                      country: 'auto',
                      line1: 'auto',
                      line2: 'auto',
                      city: 'auto',
                      state: 'auto',
                      postalCode: 'auto'
                    }
                  }
                }
              }}
            />
           </div>
          
                     {/* Save Card Option - Only show when not using saved card */}
           {!selectedSavedCard && (
          <div style={{
            marginTop: '16px',
            padding: '16px',
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '10px',
            border: '1px solid #bbf7d0'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer'
            }}>
              <input 
                type="checkbox" 
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                style={{
                  width: '16px !important',
                  height: '16px !important',
                  minWidth: '16px !important',
                  minHeight: '16px !important',
                  maxWidth: '16px !important',
                  maxHeight: '16px !important',
                  accentColor: '#16a34a',
                  transform: 'scale(0.8)',
                  cursor: 'pointer'
                }}
              />
              <span style={{
                fontSize: '14px',
                color: '#166534',
                fontWeight: 500
              }}>
                💾 Save this payment method for future purchases
              </span>
            </label>
          </div>
           )}
        </div>
      )}
      
      {/* Security Notice */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '16px',
        border: '2px solid #bbf7d0',
        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.1)'
      }}>
        <span style={{
          fontSize: '24px',
          marginRight: '12px',
          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
        }}>
          🔒
        </span>
        <p style={{
          fontSize: '16px',
          color: '#166534',
          margin: 0,
          fontWeight: 600
        }}>
          Your payment information is secure and encrypted
        </p>
      </div>

      {/* Payment Button */}
      <button
        type="submit"
        style={{
          background: loading ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
          color: '#ffffff',
          border: 'none',
          borderRadius: '16px',
          padding: '20px',
          fontSize: '20px',
          fontWeight: 700,
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          transition: 'all 0.3s ease',
          boxShadow: loading ? 'none' : '0 8px 24px rgba(16, 185, 129, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          width: '100%'
        }}
        disabled={loading}
        onMouseEnter={(e) => {
          if (!loading) {
            e.target.style.transform = 'translateY(-3px)';
            e.target.style.boxShadow = '0 12px 32px rgba(16, 185, 129, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!loading) {
            e.target.style.transform = 'none';
            e.target.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.3)';
          }
        }}
      >
        {loading ? (
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #ffffff',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        ) : (
          `Pay ${formatCurrency(amount)}`
        )}
      </button>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
};

// Stripe Checkout Form Component (copied from service provider app)
const StripeCheckoutForm = ({ amount, onSuccess, onError, onCancel, cartItems, provider, shippingAddress, specialInstructions, couponCode, couponDiscount, appliedBalance, appliedGems }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveCard, setSaveCard] = useState(true);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);



  // Check if Stripe is ready
  const isStripeReady = stripe && elements;

  // Load saved payment methods
  useEffect(() => {
    const loadSavedCards = async () => {
      try {
        const token = localStorage.getItem('customerToken');
        // PRODUCTION: Already using production URL, but ensuring consistency
      // const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/customer/payments/saved-methods`, { // COMMENTED - Already production
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/customer/payments/saved-methods`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setSavedCards(data.paymentMethods || []);
        } else {
          setSavedCards([]);
        }
              } catch (error) {
          console.error('Error loading saved cards:', error);
          setSavedCards([]);
        }
    };

    loadSavedCards();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let paymentMethodId = selectedCard;

      // If no saved card is selected, create a new payment method
      if (!selectedCard) {
        // Submit the form to validate all fields
        const { error: submitError } = await elements.submit();
        if (submitError) {
          throw new Error(submitError.message || 'Please check your payment details');
        }

        // Create payment method
        const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: {
              name: 'Customer',
              email: 'customer@example.com',
            },
          },
        });

        if (paymentMethodError) {
          throw new Error(paymentMethodError.message || 'Failed to create payment method');
        }

        paymentMethodId = paymentMethod.id;
      }

      // Process payment and create order in one step
      // PRODUCTION: Already using production URL, but ensuring consistency
      // const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com'}/api/customer/payments/process-direct`, { // COMMENTED - Already production
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/customer/payments/process-direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('customerToken')}`,
        },
        body: JSON.stringify({
          amount: Math.round(amount * 100),
          currency: getCountryFields(getCountryCode()).currency.toLowerCase(),
          paymentMethodId: paymentMethodId,
          saveCard: saveCard && !selectedCard, // Only save if not using saved card
          // Add order details for product order creation
          orderType: 'product',
          items: cartItems || [], // Get cart items from props
          shippingAddress: shippingAddress || '', // Get shipping address from props
          specialInstructions: specialInstructions || '', // Get special instructions from props
          providerId: provider?.id || provider?._id || '', // Get provider ID from props
          couponCode: couponCode || null,
          couponDiscount: couponDiscount || 0,
          appliedWalletBalance: appliedBalance || 0,
          appliedGems: appliedGems || 0,
        }),
      });

      if (!response.ok) {
        console.error('🔍 [STRIPE_FORM] Payment endpoint error:', response.status, response.statusText);
        throw new Error(`Payment failed (${response.status}): ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Payment failed');
      }

      // Pass real order data from backend to success handler
      const orderData = {
        orderId: result.data.orderId || Date.now().toString(),
        orderNumber: result.data.orderNumber || `ORD-${Date.now()}`,
        paymentIntentId: result.data.paymentIntentId,
        paymentConfirmationNumber: result.data.paymentConfirmationNumber || `PAY-${Date.now()}`,
        // Include coupon, wallet, and gems data from backend response
        appliedCoupon: result.data.appliedCoupon,
        couponDiscount: result.data.couponDiscount,
        walletBalanceUsed: result.data.walletBalanceUsed,
        gemsUsed: result.data.gemsUsed,
        metadata: result.data.metadata,
        orderDetails: {
          items: result.data.items || cartItems || [],
          subtotal: result.data.subtotal || amount,
          tax: result.data.tax || 0,
          shipping: result.data.shipping || 11,
          totalAmount: result.data.totalAmount || amount,
          status: result.data.status || 'confirmed',
          paymentStatus: result.data.paymentStatus || 'paid',
          paymentMethod: result.data.paymentMethod || 'stripe',
          estimatedDelivery: result.data.estimatedDelivery || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
          deliveryAddress: result.data.deliveryAddress || {
            street: shippingAddress || '',
            city: '',
            state: '',
            postalCode: '',
            country: 'Australia'
          },
          customerName: result.data.customerName || 'Customer',
          customerEmail: result.data.customerEmail || 'customer@example.com',
          customerPhone: result.data.customerPhone || '',
          orderDate: result.data.orderDate || new Date().toISOString(),
          paymentDate: result.data.paymentDate || new Date().toISOString(),
        }
      };

      onSuccess(orderData);

    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed');
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };



  return (
    <form onSubmit={handleSubmit} style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '20px',
      position: 'relative',
      zIndex: 1
    }}
    className="stripe-checkout-form"
    >


      {/* Error Messages */}
      {error && (
        <div style={{
          color: '#ff3b30',
          fontSize: '14px',
          margin: 0,
          padding: '8px 12px',
          background: '#fff5f5',
          borderRadius: '6px',
          border: '1px solid #fed7d7'
        }}>
          {error}
        </div>
      )}

      {/* Saved Payment Methods */}
      {savedCards.length > 0 && (
        <div style={{
          marginBottom: '20px',
          padding: '20px',
          background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            💳 Saved Payment Methods
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
            {savedCards.map((card) => (
              <div 
                key={card.id} 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  borderRadius: '8px',
                  border: selectedCard === card.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  background: selectedCard === card.id ? '#eff6ff' : '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => setSelectedCard(card.id)}
              >
                <input 
                  type="radio" 
                  checked={selectedCard === card.id}
                  onChange={() => setSelectedCard(card.id)}
                  style={{ 
                    marginRight: '12px',
                    width: '16px !important',
                    height: '16px !important',
                    minWidth: '16px !important',
                    minHeight: '16px !important',
                    maxWidth: '16px !important',
                    maxHeight: '16px !important',
                    accentColor: '#3b82f6',
                    transform: 'scale(0.8)',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: '16px' }}>
                    •••• •••• •••• {card.last4}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    Expires {card.expMonth}/{card.expYear}
                  </div>
                </div>
                <div style={{
                  padding: '4px 8px',
                  background: '#f3f4f6',
                  borderRadius: '4px',
                  fontSize: '12px',
                  textTransform: 'capitalize'
                }}>
                  {card.brand}
                </div>
              </div>
            ))}
          </div>
          
          {/* Use New Payment Method Button */}
          <button
            type="button"
            onClick={() => setSelectedCard(null)}
                         style={{
               width: '100%',
               padding: '12px',
               border: '1px solid #3b82f6',
               background: 'transparent',
               color: '#3b82f6',
               borderRadius: '8px',
               cursor: 'pointer',
               fontSize: '14px',
               fontWeight: 500
             }}
          >
            + Use New Payment Method
          </button>
        </div>
      )}

      {/* New Payment Method */}
      {!selectedCard && (
        <div style={{
          border: '2px solid #e2e8f0',
          borderRadius: '16px',
          padding: '24px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '16px'
          }}>
            {savedCards.length > 0 ? '💳 Add New Payment Method' : '💳 Payment Details'}
          </h3>
          <div style={{ 
            position: 'relative',
            zIndex: 1,
            marginBottom: '20px'
          }}>
            <PaymentElement 
              options={{
                layout: 'tabs',
                paymentMethodOrder: [
                  'card',
                  'apple_pay',
                  'google_pay',
                  'afterpay_clearpay',
                  'klarna',
                  'alipay',
                  'zip',
                  'paypal',
                  'link'
                ],
                defaultValues: {
                  billingDetails: {
                    name: 'Customer',
                    email: 'customer@example.com',
                  }
              },
              fields: {
                billingDetails: {
                  name: 'auto',
                  email: 'auto',
                  phone: 'auto',
                  address: {
                    country: 'auto',
                    line1: 'auto',
                    line2: 'auto',
                    city: 'auto',
                    state: 'auto',
                    postalCode: 'auto'
                  }
                }
              }
            }}
            />
          </div>
        
                 {/* Save Card Option */}
         <div style={{
           padding: '16px',
           background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
           borderRadius: '10px',
           border: '1px solid #bbf7d0',
           position: 'relative',
           zIndex: 1000,
           marginTop: '20px',
           pointerEvents: 'auto'
         }}>
           <label 
             style={{
               display: 'flex',
               alignItems: 'center',
               gap: '12px',
               cursor: 'pointer',
               userSelect: 'none',
               pointerEvents: 'auto'
             }}
                           onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSaveCard(!saveCard);
              }}
           >
             <input 
               type="checkbox" 
               checked={saveCard}
                               onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSaveCard(e.target.checked);
                }}
                               onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSaveCard(!saveCard);
                }}
               style={{
                 width: '16px !important',
                 height: '16px !important',
                 minWidth: '16px !important',
                 minHeight: '16px !important',
                 maxWidth: '16px !important',
                 maxHeight: '16px !important',
                 accentColor: '#16a34a',
                 transform: 'scale(0.8)',
                 cursor: 'pointer',
                 margin: 0,
                 position: 'relative',
                 zIndex: 1001,
                 pointerEvents: 'auto'
               }}
             />
             <span 
               style={{
                 fontSize: '14px',
                 color: '#166534',
                 fontWeight: 500,
                 cursor: 'pointer',
                 position: 'relative',
                 zIndex: 1001,
                 pointerEvents: 'auto'
               }}
             >
               💾 Save this payment method for future purchases
             </span>
           </label>
         </div>
        
        {/* Security Notice */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          borderRadius: '16px',
          border: '2px solid #bbf7d0',
          boxShadow: '0 4px 12px rgba(34, 197, 94, 0.1)'
        }}>
          <span style={{
            fontSize: '24px',
            marginRight: '12px',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
          }}>
            🔒
          </span>
          <p style={{
            fontSize: '16px',
            color: '#166534',
            margin: 0,
            fontWeight: 600
          }}>
            Your payment information is secure and encrypted
          </p>
        </div>
      </div>
      )}

      {/* Payment Button */}
      <button
        type="submit"
        disabled={loading || !isStripeReady}
        style={{
          background: loading || !isStripeReady ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
          color: '#ffffff',
          border: 'none',
          borderRadius: '16px',
          padding: '20px',
          fontSize: '20px',
          fontWeight: 700,
          cursor: loading || !isStripeReady ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          transition: 'all 0.3s ease',
          boxShadow: loading || !isStripeReady ? 'none' : '0 8px 24px rgba(16, 185, 129, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          opacity: loading || !isStripeReady ? 0.7 : 1,
          zIndex: 1000
        }}
        onClick={(e) => {
          if (loading || !isStripeReady) {
            e.preventDefault();
            return;
          }
        }}
        onMouseEnter={(e) => {
          if (!loading && isStripeReady) {
            e.target.style.transform = 'translateY(-3px)';
            e.target.style.boxShadow = '0 12px 32px rgba(16, 185, 129, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!loading && isStripeReady) {
            e.target.style.transform = 'none';
            e.target.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.3)';
          }
        }}
      >
        {loading ? (
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #ffffff',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        ) : !isStripeReady ? (
          'Stripe Loading...'
        ) : (
          `Pay ${formatCurrency(amount)}`
        )}
      </button>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
};

// Wrapper component with Stripe Elements
const ProductCheckoutWithStripe = (props) => {
  
  // Use a fixed amount like the service provider app to avoid initialization errors
  // The actual amount will be handled in the payment processing
  const amount = 1000; // Fixed amount in cents (like service provider app)
  
  return (
    <Elements stripe={stripePromise} options={{ 
      mode: 'payment', 
      amount: amount, // Use fixed amount to avoid initialization errors
      currency: getCountryFields(getCountryCode()).currency.toLowerCase(),
      paymentMethodCreation: 'manual',
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#3b82f6',
          colorBackground: '#ffffff',
          colorText: '#1a1a1a',
          colorDanger: '#ef4444',
          colorSuccess: '#10b981',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          spacingUnit: '4px',
          borderRadius: '12px',
          fontSizeBase: '16px',
        },
        rules: {
          '.Tab': {
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            marginBottom: '12px',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          },
          '.Tab--selected': {
            borderColor: '#3b82f6',
            backgroundColor: '#eff6ff',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
          },
          '.Tab:hover': {
            borderColor: '#3b82f6',
            backgroundColor: '#f8fafc',
          },
          '.TabLabel': {
            fontSize: '16px',
            fontWeight: '600',
            padding: '16px 20px',
            color: '#374151',
          },
          '.TabIcon': {
            width: '28px',
            height: '28px',
            marginRight: '12px',
          },
          '.TabList': {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px',
            marginBottom: '24px',
          },
          '.TabPanel': {
            padding: '24px',
            backgroundColor: '#f9fafb',
            borderRadius: '16px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          },
          '.PaymentMethodIcon': {
            width: '36px',
            height: '24px',
            objectFit: 'contain',
          },
          '.CardElement': {
            padding: '16px',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            fontSize: '16px',
            transition: 'all 0.2s ease',
          },
          '.CardElement--focus': {
            borderColor: '#3b82f6',
            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
          },
          '.CardElement--invalid': {
            borderColor: '#ef4444',
            boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
          },
          '.Input': {
            padding: '16px',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            fontSize: '16px',
            transition: 'all 0.2s ease',
          },
          '.Input--focus': {
            borderColor: '#3b82f6',
            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
          },
          '.Input--invalid': {
            borderColor: '#ef4444',
          },
          '.Label': {
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
          },
        },
      },
    }}>
      <DirectPaymentForm {...props} />
    </Elements>
  );
};

export default ProductCheckout; 